import { invariant } from '@mimir/common/util/invariant';
import type { TimelineStateUpdaterOps } from '../public/timelineStateUpdaterOps';
import type { ModelVideoTrackBox, ModelAudioTrackBox, TimelineModel, ModelTrackBox, ModelAudioTrack } from './timelineModel';
import type { SourceRef } from './sourceRef';
import type { Fraction } from '@mimir/common/util/fractionType';
import { addFractions, compareFractions, copyFrac } from '@mimir/common/util/integerMath';

/**
 * This class is used to modify the internal timeline.
 *
 * WARNING: these modifications are done directly on the
 *          underlying data structures. All sanitization
 *          must be done before invoking this API
 */
export class TimelineModelUpdater implements TimelineStateUpdaterOps {

  constructor(
    private timelineModel: TimelineModel,
  ) {}

  addVideoTrack(): void {
    this.timelineModel.videoTracks.push([]);
  }

  addAudioTrack(): void {
    this.timelineModel.audioTracks.push([]);
  }

  addSource(sourceRef: SourceRef): void {
    invariant(this.timelineModel.sourceRefs[sourceRef.srcId] === undefined, () => `Should not have source ref ${sourceRef.srcId}`);
    this.timelineModel.sourceRefs[sourceRef.srcId] = sourceRef;
    this.timelineModel.trackBoxesBySourceId[sourceRef.srcId] = [];
  }

  removeSource(sourceRef: SourceRef): void {
    invariant(this.timelineModel.sourceRefs[sourceRef.srcId] !== undefined, () => `Should have source ref ${sourceRef.srcId}`);
    delete this.timelineModel.sourceRefs[sourceRef.srcId];
    delete this.timelineModel.trackBoxesBySourceId[sourceRef.srcId];
  }

  updateUndoRedoStatus(_canUndo: boolean, _canRedo: boolean): void {}

  insertVideoTrackBox(trackIndex: number, index: number, box: ModelVideoTrackBox): void {
    this.timelineModel.trackBoxesById[box.boxId] = box;
    this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId].push(box);
    this.timelineModel.videoTracks[trackIndex].splice(index, 0, box);
  }

  removeVideoTrackBox(trackIndex: number, box: ModelVideoTrackBox): void {
    delete this.timelineModel.trackBoxesById[box.boxId];

    const index0 = this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId].findIndex(b => b.boxId === box.boxId);
    invariant(index0 !== -1, () => `Could not find video track box ${box.boxId} for source ${box.sourceRef.srcId}`);
    this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId].splice(index0, 1);

    const index1 = this.timelineModel.videoTracks[trackIndex].findIndex(b => b.boxId === box.boxId);
    invariant(index1 !== -1, () => `Could not find video track box ${box.boxId} on track ${trackIndex}`);
    this.timelineModel.videoTracks[trackIndex].splice(index1, 1);
  }

  addAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox): void {
    this.timelineModel.trackBoxesById[box.boxId] = box;
    this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId].push(box);
    this.timelineModel.audioTracks[trackIndex].push(box);
    this.sortAudioTrack(this.timelineModel.audioTracks[trackIndex]);
  }

  removeAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox): void {
    delete this.timelineModel.trackBoxesById[box.boxId];

    const index0 = this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId].findIndex(b => b.boxId === box.boxId);
    invariant(index0 !== -1, () => `Could not find audio track box ${box.boxId} for source ${box.sourceRef.srcId}`);
    this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId].splice(index0, 1);

    const index1 = this.timelineModel.audioTracks[trackIndex].findIndex(b => b.boxId === box.boxId);
    invariant(index1 !== -1, () => `Could not find audio track box ${box.boxId} on track ${trackIndex}`);
    this.timelineModel.audioTracks[trackIndex].splice(index1, 1);
    this.sortAudioTrack(this.timelineModel.audioTracks[trackIndex]);
  }

  setOutPoint(box: ModelTrackBox, outPoint: Fraction): void {
    box.outPointInSeconds = {
      numerator: outPoint.numerator,
      denominator: outPoint.denominator,
    };
  }

  setInPoint(box: ModelTrackBox, inPoint: Fraction): void {
    box.inPointInSeconds = {
      numerator: inPoint.numerator,
      denominator: inPoint.denominator,
    };
  }


  private sortAudioTrack(audioTrack: ModelAudioTrack) {
    audioTrack.sort((a: ModelAudioTrackBox, b: ModelAudioTrackBox) => {
      const aStart = addFractions(a.sourceRef.mediaStartOffset, a.inPointInSeconds);
      const bStart = addFractions(b.sourceRef.mediaStartOffset, b.inPointInSeconds);
      const cmp = compareFractions(aStart, bStart);
      if (cmp !== 0) {
        return cmp;
      }
      return (a.boxId < b.boxId) ? -1 : 1;
    });
  }


  sortTracks() {
    for (const audioTrack of this.timelineModel.audioTracks) {
      this.sortAudioTrack(audioTrack);
    }
  }


  setSourceStart(sourceRef: SourceRef, start: Fraction): void {
    sourceRef.mediaStartOffset = copyFrac(start);
  }


  setBoxStart(box: ModelTrackBox, start: Fraction): void {
    // Nothing to do
  }


  setSourceDragged(sourceRef: SourceRef, dragged: boolean): void {
    // Nothing to do
  }
}
